// Every decision the student dashboard makes, away from the markup and tested on
// its own (`__tests__/components/Dashboard/studentHomeRules.test.js`).

export const clampPct = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
};

const nameParts = (name) => String(name || '').trim().split(/\s+/).filter(Boolean);

export const firstNameOf = (name) => nameParts(name)[0] || 'Learner';

export const initialsOf = (name, fallback = 'U') => (
  nameParts(name).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || fallback
);

export const greetingFor = (hour) => {
  if (hour >= 5 && hour < 12) return 'Good morning';
  if (hour >= 12 && hour < 17) return 'Good afternoon';
  return 'Good evening';
};

/** The student's week runs Monday to Sunday; this is today's place in it. */
export const weekIndexOf = (date = new Date()) => (date.getDay() + 6) % 7;

// ── What the plan sells ───────────────────────────────────────────────────────

/** `isOn` is the sidebar's own test: not hidden, and on the plan. */
export const homeModules = (isOn) => {
  const aiLearning = isOn('aiLearning');
  const aiMock = isOn('aiMock');
  return {
    attendance: isOn('attendanceManagement'),
    aiLearning,
    aiMock,
    ai: aiLearning || aiMock || isOn('aiDoubtSolving'),
    coding: isOn('codingPractice'),
    fees: isOn('feeManagement'),
    interviews: isOn('interviews'),
    quizzes: isOn('quizAssessment'),
    selfPaced: isOn('selfPacedCourses'),
  };
};

// ── Stat tiles ────────────────────────────────────────────────────────────────

/**
 * Attendance is what My Attendance says (`/attendance/my-summary`): its
 * denominator is the classes HELD. The summary's own figure counts only sessions
 * the student joined, so someone who skipped nineteen of twenty read 100 %.
 * `overall: null` means no class has been held yet — no figure, never 0 %.
 */
export const attendanceMetric = (fromSummary, mySummary) => {
  if (!mySummary) return fromSummary;
  if (mySummary.overall === null || mySummary.overall === undefined) {
    return { value: null, label: 'No classes yet' };
  }
  const value = clampPct(mySummary.overall);
  let label = 'Needs Focus';
  if (value >= 85) label = 'Excellent';
  else if (value >= 65) label = 'Good';
  return { value, label };
};

export const attendanceTone = (value) => {
  if (value === null || value === undefined) return 'neutral';
  if (value >= 85) return 'emerald';
  if (value >= 65) return 'sky';
  return 'amber';
};

export const statTiles = (metrics, modules) => {
  const { courseProgress, attendance, aiAttempts, mockInterviews } = metrics;
  const attendanceKnown = attendance.value !== null && attendance.value !== undefined;
  const tiles = [{
    key: 'progress',
    title: 'Course Progress',
    tone: 'violet',
    value: `${clampPct(courseProgress.value)}%`,
    progress: clampPct(courseProgress.value),
    sub: courseProgress.label,
    to: '/s/learning',
  }];
  if (modules.attendance) {
    tiles.push({
      key: 'attendance',
      title: 'Attendance',
      tone: 'emerald',
      standing: attendanceTone(attendance.value),
      value: attendanceKnown ? `${clampPct(attendance.value)}%` : '—',
      progress: attendanceKnown ? clampPct(attendance.value) : undefined,
      sub: attendance.label,
      to: '/s/attendance',
    });
  }
  if (modules.aiLearning) {
    tiles.push({
      key: 'ai',
      title: 'AI Attempts',
      tone: 'violet',
      value: aiAttempts.value,
      suffix: `/ ${aiAttempts.total}`,
      sub: aiAttempts.label,
      to: '/s/learn-ai/history',
    });
  }
  if (modules.aiMock) {
    tiles.push({
      key: 'mock',
      title: 'Mock Interviews',
      tone: 'rose',
      value: mockInterviews.value,
      sub: mockInterviews.label,
      to: '/s/mock-interview/history',
    });
  }
  return tiles;
};

// ── Level and streak ──────────────────────────────────────────────────────────

export const levelPct = (level) => {
  if (!level?.xpToNext) return 0;
  return clampPct((level.xp / level.xpToNext) * 100);
};

/** A line that is true of this student's week, not a slogan shown to everyone. */
export const momentumFor = (streak) => {
  const days = streak?.days || 0;
  const activeDays = (streak?.week || []).filter(Boolean).length;
  if (days >= 7) {
    return { title: `${days}-day streak!`, sub: 'A full week and counting. Keep it alive today.' };
  }
  if (days >= 2) {
    return { title: 'Keep the momentum going!', sub: `You're on a ${days}-day streak.` };
  }
  if (activeDays > 0) {
    const word = activeDays === 1 ? 'day' : 'days';
    return { title: 'Good start this week', sub: `Active on ${activeDays} ${word} so far.` };
  }
  return { title: 'Start your streak today', sub: 'One short session gets it going.' };
};

export const streakMessage = (days) => {
  if (!days) return 'Start your streak today!';
  if (days === 1) return 'Come back tomorrow to build it.';
  return "You're on a roll!";
};

// ── Courses ───────────────────────────────────────────────────────────────────

export const canJoin = (course) => course.kind === 'batch' && !!course.batchId && !!course.isLive;

/** The live class first: it must never sit behind "View all". */
export const orderCourses = (courses) => (
  [...courses].sort((a, b) => Number(canJoin(b)) - Number(canJoin(a)))
);

export const unitsLabel = (course) => {
  if (!course.totalUnits) return '';
  const word = course.kind === 'self-paced' ? 'lessons' : 'classes';
  return `${course.completedUnits || 0} of ${course.totalUnits} ${word}`;
};

export const courseKinds = (courses) => {
  const live = courses.filter((c) => c.kind === 'batch');
  const selfPaced = courses.filter((c) => c.kind === 'self-paced');
  const kinds = [];
  if (live.length) kinds.push({ value: 'batch', label: 'Live', count: live.length, courses: orderCourses(live) });
  if (selfPaced.length) kinds.push({ value: 'self-paced', label: 'Self-paced', count: selfPaced.length, courses: selfPaced });
  return kinds;
};

// ── Upcoming and assignments ──────────────────────────────────────────────────

const UPCOMING_ROUTE = {
  assignment: '/s/assignments',
  mock: '/s/interviews',
  quiz: '/s/assessments',
  class: '/s/calendar',
};

/** A row with no link of its own is a coding assignment, which needs that module. */
const reachableAssignment = (row, modules) => !!row.link || modules.coding;

export const upcomingFor = (items, modules) => items.filter((item) => {
  if (item.type === 'quiz') return modules.quizzes;
  if (item.type === 'mock') return modules.interviews;
  if (item.type === 'assignment') return reachableAssignment(item, modules);
  return true;
});

export const upcomingLink = (item) => item.link || UPCOMING_ROUTE[item.type] || '/s/calendar';

/** Red is for work that is nearly due. A class tomorrow is not an emergency. */
export const isUrgent = (item) => !!item.urgent && item.type !== 'class';

export const assignmentsFor = (rows, modules) => rows.filter((row) => reachableAssignment(row, modules));

export const assignmentLink = (row) => row.link || '/s/assignments';

// ── Performance ───────────────────────────────────────────────────────────────

/**
 * `n` is how many scores a point stands on. Older payloads have no `n` and send 0
 * for an empty day, which is no data rather than a score of 0.
 */
export const trendPoints = (trend = []) => trend.map((point) => {
  const empty = point.n === undefined ? !point.s : point.n === 0;
  return { label: point.w, value: empty ? null : clampPct(point.s) };
});

/**
 * A point's centre, in the chart's 0–100 box. Each sits mid-column, above its label.
 * The axis hugs the scores: on a full 0–100 scale a week of 64 to 80 reads as flat.
 * Every value is printed under the chart, so the shape cannot mislead on its own.
 */
const plotted = (points) => {
  const values = points.map((point) => point.value).filter((value) => value !== null);
  const low = Math.max(0, Math.min(...values) - 15);
  const high = Math.min(100, Math.max(...values) + 10);
  return points
    .map((point, i) => ({ ...point, x: ((i + 0.5) / points.length) * 100 }))
    .filter((point) => point.value !== null)
    .map((point) => ({ ...point, y: ((high - point.value) / (high - low || 1)) * 100 }));
};

const round = (n) => Math.round(n * 100) / 100;

/**
 * The line through the days that have scores, and the area under it. Each curve
 * leaves and arrives level, so it never overshoots a real score.
 */
export const trendPaths = (points) => {
  const dots = plotted(points);
  if (dots.length < 2) return { dots, line: '', area: '' };
  const start = `M ${round(dots[0].x)} ${round(dots[0].y)}`;
  const curves = dots.slice(1).map((dot, i) => {
    const prev = dots[i];
    const mid = round((prev.x + dot.x) / 2);
    return `C ${mid} ${round(prev.y)}, ${mid} ${round(dot.y)}, ${round(dot.x)} ${round(dot.y)}`;
  });
  const line = [start, ...curves].join(' ');
  const last = dots[dots.length - 1];
  return { dots, line, area: `${line} L ${round(last.x)} 100 L ${round(dots[0].x)} 100 Z` };
};

export const trendSummary = (points) => {
  const known = points.filter((point) => point.value !== null);
  if (!known.length) return 'No scores in this period.';
  return `Scores: ${known.map((point) => `${point.label} ${point.value}%`).join(', ')}.`;
};

export const subjectTone = (label) => {
  const text = String(label || '').toLowerCase();
  if (text.includes('excellent')) return 'emerald';
  if (text.includes('good')) return 'sky';
  if (text.includes('needs')) return 'amber';
  return 'primary';
};

export const reportStanding = (score) => {
  if (score >= 75) return { tone: 'emerald', label: 'On Track', rising: true };
  if (score >= 50) return { tone: 'amber', label: 'Improving', rising: true };
  return { tone: 'rose', label: 'Needs Focus', rising: false };
};

/** Where the bars of the AI usage chart stand; an idle hour keeps a visible stub. */
export const usageBars = (insight) => {
  const hours = insight?.hours?.length === 24
    ? insight.hours
    : Array.from({ length: 24 }, (_, h) => ({ h, v: 0, peak: false }));
  const max = Math.max(...hours.map((hour) => hour.v), 0);
  return {
    hasData: !!insight?.hasData && max > 0,
    bars: hours.map((hour) => ({
      hour: hour.h,
      height: max > 0 && hour.v > 0 ? Math.max(12, Math.round((hour.v / max) * 100)) : 6,
      peak: !!hour.peak && max > 0,
    })),
  };
};
